<script setup lang="ts">
/**
 * The five password requirements, as chips (Figma `66:47383` / `66:47425`).
 *
 * Deliberately has no error state: while the user types, an unmet rule is
 * neutral grey with a cross, never red (`66:47508`). Red here would shout at
 * someone who is halfway through a perfectly good password.
 *
 * Rendered as DS Tags (static status, no interaction): unmet = Neutral with
 * close-small, met = Positive with check-small.
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { evaluatePassword, PASSWORD_RULES } from '@/features/auth/model/passwordRules'
import { FairTag } from '@/ui/primitives/FairTag'

const props = defineProps<{
  password: string
  visible: boolean
}>()

const { t } = useI18n()

const chips = computed(() => {
  const state = evaluatePassword(props.password)
  return PASSWORD_RULES.map((rule) => ({
    id: rule.id,
    label: t(rule.labelKey),
    met: state[rule.id],
  }))
})
</script>

<template>
  <ul
    v-if="visible"
    class="fair-auth-rules"
    aria-live="polite"
  >
    <li
      v-for="chip in chips"
      :key="chip.id"
      class="fair-auth-rules__item"
    >
      <FairTag
        :tone="chip.met ? 'positive' : 'neutral'"
        :leading-icon="chip.met ? 'check-small' : 'close-small'"
        :label="chip.label"
      />
    </li>
  </ul>
</template>
